import useAgents from "hooks-for/agents/use-agents";
import { FunctionComponent, memo, useMemo } from "react";
import { IAgent } from "@edgetier/watchtower-types";
import { IProps } from "./select-agent.types";
import { groupBy } from "@edgetier/utilities";
import FilteredSelectField from "../filtered-select-field";
import "./select-agent.scss";

/**
 * Filter for agents.
 * @param props.mustInclude Agent ID(s) that have to be included in the select menu.
 * @param props.name        Field name.
 * @param props.parameters  Parameters to send along with the request for agents.
 * @returns          Agents select.
 */
const SelectAgent: FunctionComponent<IProps> = ({ mustInclude, name = "agentId", parameters, ...otherProps }) => {
    const { data: allAgents, isLoading: areAllAgentsLoading } = useAgents();

    const selectedAgents = useMemo(() => {
        const mustIncludeArray = typeof mustInclude === "string" ? [mustInclude] : mustInclude ?? [];
        return (allAgents ?? []).filter(({ agentId }) => mustIncludeArray.includes(agentId));
    }, [allAgents, mustInclude]);

    const { data: filteredAgents, isLoading } = useAgents(parameters, {
        select: (agents) => {
            const groupedAgents = groupBy(agents, ({ agentId }) => agentId);

            const agentsToAdd = selectedAgents.filter(({ agentId }) => agentId in groupedAgents === false);

            return [...agents, ...agentsToAdd];
        },
    });

    return (
        <FilteredSelectField<IAgent, string>
            description="agent"
            getLabel={({ email, name }) => (typeof email === "string" ? `${name} (${email})` : name)}
            getValue={({ agentId }) => agentId}
            isLoading={isLoading || areAllAgentsLoading}
            allOptions={allAgents ?? []}
            filteredOptions={filteredAgents}
            name={name}
            {...otherProps}
        >
            {({ email, name }) => (
                <>
                    {name} {typeof email === "string" && <div className="select-agent__email">{email}</div>}
                </>
            )}
        </FilteredSelectField>
    );
};

export default memo(SelectAgent);
